import React, { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { projectCountriesQuery } from "./dashboard.queries";
import {
  buildCountriesUsersRankedList,
  buildCountriesViewsRankedList,
} from "./dashboard.utils";
import MapViewToggle from "./MapViewToggle";
import RankedQueryCard from "../charts/RankedQueryCard";

import type { JSX } from "react";

import type { GetProjectCountriesResponse } from "@/src/api/StatsAPI.types";
import type { AppState } from "@/src/reducers";

import type { CountriesViewProps } from "./dashboard.types";
import type {
  ChartSeries,
  PaidFreeSeriesKey,
  UsersSeriesKey,
} from "../charts/charts.types";

function CountriesRankedCard({
  period,
  view,
  onViewChange,
}: CountriesViewProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const countries = useSelector(
    (state: AppState) => state.fetchReducer.countries,
  );
  const query = projectCountriesQuery(projectSlug, period);

  const viewsSeriesList = useMemo(
    (): ChartSeries<PaidFreeSeriesKey>[] => [
      {
        key: "paid",
        label: t("charts.series.paid"),
        color: "var(--chart-1)",
      },
      {
        key: "free",
        label: t("charts.series.free"),
        color: "var(--chart-2)",
      },
    ],
    [t],
  );
  const usersSeriesList = useMemo(
    (): ChartSeries<UsersSeriesKey>[] => [
      { key: "users", label: t("users"), color: "var(--chart-1)" },
    ],
    [t],
  );
  const buildViewsRankedList = useCallback(
    (response: GetProjectCountriesResponse | undefined) =>
      buildCountriesViewsRankedList(response, countries, lang),
    [countries, lang],
  );
  const buildUsersRankedList = useCallback(
    (response: GetProjectCountriesResponse | undefined) =>
      buildCountriesUsersRankedList(response, countries, lang),
    [countries, lang],
  );
  const toggle = (
    <MapViewToggle
      ariaLabel={t("charts.countriesRanking")}
      onChange={onViewChange}
      value={view}
    />
  );

  return view === "views" ? (
    <RankedQueryCard
      buildRankedList={buildViewsRankedList}
      extraActions={toggle}
      query={query}
      seriesList={viewsSeriesList}
      title={t("charts.countriesRanking")}
    />
  ) : (
    <RankedQueryCard
      buildRankedList={buildUsersRankedList}
      extraActions={toggle}
      query={query}
      seriesList={usersSeriesList}
      title={t("charts.countriesRanking")}
    />
  );
}

export default CountriesRankedCard;
